import React, { useState, useCallback } from 'react';
import { Spin, Modal, Input, Button, Tabs } from 'antd';
import { useT206Mode } from './hooks/useT206Mode';
import { useT206Record } from './hooks/useT206Record';
import ActionBar from './components/ActionBar';
import Tab1MeetingInfo from './components/Tab1MeetingInfo';
import Tab2OurAttendees from './components/Tab2OurAttendees';
import Tab3CustomerAttendees from './components/Tab3CustomerAttendees';
import Tab4Topics from './components/Tab4Topics';
import './T206Form.css';

const T206Form: React.FC = () => {
  const { mode, isEditing, enterAdd, enterEdit, enterBrowse } = useT206Mode();
  const {
    records,
    current,
    currentIdx,
    formData,
    loading,
    setField,
    navFirst,
    navPrev,
    navNext,
    navLast,
    startAdd,
    startEdit,
    cancel,
    save,
    doDelete,
    quickSearch,
  } = useT206Record(mode, enterBrowse);

  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('tab1');

  const handleAdd = useCallback(() => {
    startAdd();
    enterAdd();
    setActiveTab('tab1');
  }, [startAdd, enterAdd]);

  const handleEdit = useCallback(() => {
    startEdit();
    enterEdit();
    setActiveTab('tab1');
  }, [startEdit, enterEdit]);

  const handleDelete = useCallback(() => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除會議記錄「${current.ft160_01}」？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  }, [current, doDelete]);

  const handleSearch = useCallback(() => {
    quickSearch(searchText.trim());
  }, [searchText, quickSearch]);

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';
  const recordCount =
    records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料';

  const tabItems = [
    {
      key: 'tab1',
      label: '會議基本資訊',
      children: (
        <Tab1MeetingInfo
          formData={formData}
          isEditing={isEditing}
          setField={setField}
        />
      ),
    },
    {
      key: 'tab2',
      label: `我司參加人員 (${formData.our_attendees.length})`,
      children: (
        <Tab2OurAttendees
          attendees={formData.our_attendees}
          isEditing={isEditing}
          setField={setField}
        />
      ),
    },
    {
      key: 'tab3',
      label: `客戶參加人員 (${formData.customer_attendees.length})`,
      children: (
        <Tab3CustomerAttendees
          attendees={formData.customer_attendees}
          isEditing={isEditing}
          setField={setField}
        />
      ),
    },
    {
      key: 'tab4',
      label: `會議議題 (${formData.topics.length})`,
      children: (
        <Tab4Topics
          topics={formData.topics}
          isEditing={isEditing}
          setField={setField}
        />
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div className="t206-form">
        {/* Title */}
        <div className="t206-header">
          <span className="t206-title">客戶會議記錄 (pwt206f1)</span>
          <span className="t206-meta">
            [{modeLabel}] {recordCount}
          </span>
        </div>

        {/* Search Bar */}
        <div className="t206-search-bar">
          <Input
            placeholder="輸入會議主題或代號快速查詢"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            disabled={isEditing}
            style={{ width: 320 }}
            allowClear
          />
          <Button onClick={handleSearch} disabled={isEditing}>
            查詢
          </Button>
        </div>

        {/* Navigation Bar */}
        <div className="t206-nav-bar">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
          {current && mode === 'browse' && (
            <span className="t206-record-id">
              會議代號: {current.ft160_01}
            </span>
          )}
        </div>

        {/* Action Bar */}
        <div className="t206-action-bar">
          <ActionBar
            mode={mode}
            hasCurrent={!!current}
            loading={loading}
            onAdd={handleAdd}
            onSave={save}
            onEdit={handleEdit}
            onCancel={cancel}
            onDelete={handleDelete}
            onSearch={handleSearch}
          />
        </div>

        {/* Tabs */}
        <div className="t206-tabs-container">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            size="small"
          />
        </div>
      </div>
    </Spin>
  );
};

export default T206Form;
